import { SubscriptionData, UserRole } from '@avanzu/types';
import { Alert, Box, Button, Divider, Link, Stack, TextField, Typography } from '@mui/material';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import React, { useEffect, useState } from 'react';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import PaperSection from '../../components/PaperSection';
import PlanSelector from '../../components/PlanSelector';
import ContentWrapper from '../../layout/ContentWrapper';
import { fetchSubscription } from '../../services/subscription';
import { apiClient } from '../../services/apiClient';
import { useNavigate } from 'react-router-dom';

export const PlanRoles = [UserRole.AVANZU, UserRole.OWNER, UserRole.ADMIN];

const Plan: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null);

  // Load legacy subscription data (if any) — silently degrades when none
  // exists. In the per-project billing model there are no subscriptions;
  // PlanSelector uses currentOrganisation.currentTierProductId.
  const loadSubscription = async () => {
    try {
      setError(null);
      const data = await fetchSubscription();
      setSubscriptionData(data);
    } catch {
      setSubscriptionData(null);
    }
  };

  // Check whether the org has at least one card on file
  const loadPaymentMethodStatus = async () => {
    const organisationId = localStorage.getItem('organisationId') || '';
    if (!organisationId) {
      setHasPaymentMethod(false);
      return;
    }
    try {
      const response = await apiClient.get(
        `/organisations/${organisationId}/stripe/payment-methods/list`
      );
      if (!response.ok) {
        setHasPaymentMethod(false);
        return;
      }
      const data = await response.json();
      const methods = data?.paymentMethods ?? data?.data ?? data ?? [];
      setHasPaymentMethod(Array.isArray(methods) ? methods.length > 0 : false);
    } catch {
      setHasPaymentMethod(false);
    }
  };

  const handlePlanChanged = async () => {
    await Promise.all([loadSubscription(), loadPaymentMethodStatus()]);
  };

  // Referral / promotion code
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const handleApplyPromoCode = async () => {
    const organisationId = localStorage.getItem('organisationId') || '';
    if (!organisationId || !promoCode.trim()) return;

    setPromoLoading(true);
    setPromoMessage(null);
    try {
      const response = await apiClient.post(
        `/organisations/${organisationId}/stripe/promotion-codes/apply`,
        { code: promoCode.trim() }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to apply code');
      }
      const percent = data.discountRateBps ? Math.round(data.discountRateBps / 100) : 0;
      setPromoMessage({
        severity: 'success',
        text: percent
          ? `Code ${data.promotionCode} applied — ${percent}% off your first paid project.`
          : `Code ${data.promotionCode} applied.`,
      });
      setPromoCode('');
    } catch (err) {
      setPromoMessage({
        severity: 'error',
        text: err instanceof Error ? err.message : 'Failed to apply code',
      });
    } finally {
      setPromoLoading(false);
    }
  };

  const handlePaymentMethodRequired = () => {};

  useEffect(() => {
    const init = async () => {
      await Promise.all([loadSubscription(), loadPaymentMethodStatus()]);
    };
    init();
  }, []);

  return (
    <ContentWrapper breadcrumb>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {hasPaymentMethod === false && (
        <Box sx={{ mb: 2 }}>
          <EmptyStateContent
            icon={<CreditCardOutlinedIcon />}
            title="Payment method needed"
            message="Add your credit card before selecting a plan below."
            button={
              <Button variant="contained" onClick={() => navigate('/settings/billing')}>
                Add Card
              </Button>
            }
          />
        </Box>
      )}

      {/* Available plans */}
      <PaperSection
        title="Available Plans"
        action={
          <Button component="a" href="/settings/billing">
            Edit Payment Card
          </Button>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Avanzu is priced per project per financial year. Start as early as you want with no
          recurring subscriptions.
        </Typography>
        <PlanSelector
          currentSubscription={subscriptionData}
          onPlanChanged={handlePlanChanged}
          onPaymentMethodRequired={handlePaymentMethodRequired}
          showCurrentPlanChip={true}
        />
        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          Looking for invoices?{' '}
          <Link href="/settings/billing" underline="hover">
            See billing and cards
          </Link>
          .
        </Typography>
      </PaperSection>

      {/* Referral / promotion code */}
      <PaperSection title="Referral code">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Were you referred by a partner? Enter their code to get a discount on your first paid
          project.
        </Typography>
        {promoMessage && (
          <Alert severity={promoMessage.severity} sx={{ mb: 2 }} onClose={() => setPromoMessage(null)}>
            {promoMessage.text}
          </Alert>
        )}
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            label="Referral code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <Button
            variant="outlined"
            onClick={handleApplyPromoCode}
            disabled={promoLoading || !promoCode.trim()}
          >
            Apply
          </Button>
        </Stack>
      </PaperSection>
    </ContentWrapper>
  );
};

export default Plan;
